/**
 * EEO (Equal Employment Opportunity) DEMOGRAPHIC TYPES
 *
 * Types for collecting and reporting EEO demographic information
 * as required by EEOC guidelines.
 *
 * @see https://www.eeoc.gov/statistics/introduction-race-and-ethnic-hispanic-origin-data-census-2000-special-eeo-file
 * @see https://www.indeed.com/hire/c/info/eeo-categories
 */

/**
 * EEO ethnicity categories (long form)
 */
export const EEOEthnicityCategoriesLookup = {
  White: "White (Not Hispanic or Latino)",
  Black: "Black or African American (Not Hispanic or Latino)",
  Hispanic: "Hispanic or Latino",
  AmericanIndian: "American Indian or Alaska Native (Not Hispanic or Latino)",
  Asian: "Asian (Not Hispanic or Latino)",
  NativeHawaiian: "Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)",
  TwoOrMore: "Two or More Races (Not Hispanic or Latino)",
  PreferNotToDisclose: "Prefer Not To Disclose",
} as const;

export type EEOEthnicityCategory =
  (typeof EEOEthnicityCategoriesLookup)[keyof typeof EEOEthnicityCategoriesLookup];

export type EEOEthnicityCategoryShort = keyof typeof EEOEthnicityCategoriesLookup;

export const EEOEthnicityShortLookup: Record<EEOEthnicityCategory, EEOEthnicityCategoryShort> = {
  "White (Not Hispanic or Latino)": "White",
  "Black or African American (Not Hispanic or Latino)": "Black",
  "Hispanic or Latino": "Hispanic",
  "American Indian or Alaska Native (Not Hispanic or Latino)": "AmericanIndian",
  "Asian (Not Hispanic or Latino)": "Asian",
  "Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)": "NativeHawaiian",
  "Two or More Races (Not Hispanic or Latino)": "TwoOrMore",
  "Prefer Not To Disclose": "PreferNotToDisclose",
};

export const EEOMinorityEthnicities: EEOEthnicityCategoryShort[] = [
  "Black",
  "Hispanic",
  "AmericanIndian",
  "Asian",
  "NativeHawaiian",
  "TwoOrMore",
];

/**
 * EEO ethnicity categories (long form) as an array
 */
export const EEOEthnicityCategories = Object.values(EEOEthnicityCategoriesLookup);

/**
 * EEO gender categories
 */
export const EEOGenderCategories = ["Male", "Female", "Non Binary", "Prefer Not To Disclose"] as const;

export type EEOGenderCategory = (typeof EEOGenderCategories)[number];

/**
 * Gender options for forms (excludes "Non Binary" for backwards compatibility)
 * @deprecated Consider using EEOGenderCategories instead
 */
export const EEOGenderFormOptions = ["Male", "Female", "Prefer Not To Disclose"] as const;

/**
 * EEO job categories
 * @see https://www.eeoc.gov/sites/default/files/migrated_files/employers/eeo1/jobclassguide.pdf
 * @see https://hrdailyadvisor.blr.com/2012/08/07/understanding-eeo-job-categories-for-the-eeo-1-report/
 */
// Note: intentionally NOT `as const` to preserve backwards compatibility
// with consumers that use `EEOJobCategories.includes()` with potentially unknown values
export const EEOJobCategories = [
  "Executive/Senior Level Officials and Managers",
  "First/Mid Level Officials and Managers",
  "Professionals",
  "Technicians",
  "Sales Workers",
  "Administrative Support Workers",
  "Craft Workers",
  "Operatives",
  "Laborers and Helpers",
  "Service Workers",
  "Prefer Not To Disclose",
];

export type EEOJobCategory = (typeof EEOJobCategories)[number];

/**
 * EEO skill levels
 */
export const EEOSkillLevels = ["apprentice", "journeyman", "unclassified"] as const;

export type EEOSkillLevel = (typeof EEOSkillLevels)[number];

/**
 * Marital status options
 */
export const MaritalStatusOptions = [
  "Single",
  "Married",
  "Divorced",
  "Widowed",
  "Separated",
  "Prefer Not To Disclose",
] as const;

export type MaritalStatus = (typeof MaritalStatusOptions)[number];

/**
 * EEO veteran status categories
 */
export const EEOVeteranStatusOptions = [
  "Identifies as a disabled veteran",
  "Identifies as a protected veteran",
  "Identifies as a recently separated veteran",
  "Identifies as an active duty wartime or campaign badge veteran",
  "Identifies as an armed forces service medal veteran",
  "Not a protected veteran",
  "Prefer Not To Disclose",
] as const;

export type EEOVeteranStatus = (typeof EEOVeteranStatusOptions)[number];

/**
 * EEO disability status categories
 */
export const EEODisabilityStatusOptions = [
  "Yes, I have a disability (or previously had a disability)",
  "No, I do not have a disability and have not had one in the past",
  "Prefer Not To Disclose",
] as const;

export type EEODisabilityStatus = (typeof EEODisabilityStatusOptions)[number];

/**
 * Demographic information collected for EEO reporting purposes.
 * Used on both team members and job application candidates.
 */
export type DemographicInfo = {
  ethnicity?: EEOEthnicityCategory;
  gender?: EEOGenderCategory;
  job_category?: EEOJobCategory;
  veteran_status?: EEOVeteranStatus;
  marital_status?: MaritalStatus;
  disability_status?: EEODisabilityStatus;
};

/**
 * Alias for DemographicInfo used in team member contexts.
 * @deprecated Use DemographicInfo instead
 */
export type TeamMemberDemographicInfo = DemographicInfo;
